import { Permissions as P, type Member } from '@comms/shared';
import { useStore } from '../store';
import { openModal } from '../store/ui';
import { permissionsFor, has } from '../hooks/permissions';
import { Avatar } from './ui';
import { BotTag } from './modals/Bots';

export function MemberList({ guildId, channelId }: { guildId: string; channelId: string }) {
  const state = useStore();
  const guild = state.guilds[guildId];
  const members = Object.values(state.members[guildId] ?? {});
  if (!guild) return null;
  // Only list members who can see this channel.
  const channel = state.channels[channelId];
  const visible = members.filter((m) => {
    if (!channel?.permissionOverwrites.length) return true;
    return has(
      permissionsFor({ ...state, me: { ...state.me!, id: m.user.id } }, guildId, channelId),
      P.VIEW_CHANNEL,
    );
  });
  const hoisted = guild.roles
    .filter((r) => r.hoist && r.id !== guildId)
    .sort((a, b) => b.position - a.position);
  const online = (m: Member) => (state.presences[m.user.id]?.status ?? 'offline') !== 'offline';
  const groups: { title: string; color?: string; list: Member[] }[] = [];
  const placed = new Set<string>();
  for (const role of hoisted) {
    const list = visible.filter(
      (m) => online(m) && !placed.has(m.user.id) && m.roles.includes(role.id),
    );
    list.forEach((m) => placed.add(m.user.id));
    if (list.length)
      groups.push({
        title: role.name,
        color: role.color ? `#${role.color.toString(16).padStart(6, '0')}` : undefined,
        list,
      });
  }
  groups.push({
    title: 'Online',
    list: visible.filter((m) => online(m) && !placed.has(m.user.id)),
  });
  groups.push({ title: 'Offline', list: visible.filter((m) => !online(m)) });
  const name = (m: Member) => m.nickname ?? m.user.displayName;

  return (
    <aside className="w-60 shrink-0 overflow-y-auto bg-bg-1 px-2 py-4">
      {groups
        .filter((g) => g.list.length > 0)
        .map((g) => (
          <div key={g.title} className="mb-4">
            <h3 className="mb-1 px-2 text-xs font-bold uppercase tracking-wide text-text-3">
              {g.title} — {g.list.length}
            </h3>
            {g.list
              .sort((a, b) => name(a).localeCompare(name(b)))
              .map((m) => {
                const color = guild.roles
                  .filter((r) => m.roles.includes(r.id) && r.color)
                  .sort((a, b) => b.position - a.position)[0]?.color;
                const presence = state.presences[m.user.id];
                return (
                  <button
                    key={m.user.id}
                    type="button"
                    onClick={() => openModal({ kind: 'profile', userId: m.user.id, guildId })}
                    className={`flex w-full items-center gap-3 rounded px-2 py-1.5 text-left hover:bg-bg-hover ${online(m) ? '' : 'opacity-40'}`}
                  >
                    <Avatar userId={m.user.id} size={32} showStatus />
                    <span className="min-w-0">
                      <span
                        className="block truncate text-sm font-medium"
                        style={{
                          color: color ? `#${color.toString(16).padStart(6, '0')}` : undefined,
                        }}
                      >
                        {name(m)}
                        {m.user.bot && <BotTag />}{' '}
                        {guild.ownerId === m.user.id && <span title="Owner">👑</span>}
                      </span>
                      {presence?.customStatus && (
                        <span className="block truncate text-xs text-text-3">
                          {presence.customStatus}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
          </div>
        ))}
    </aside>
  );
}
